import { useCallback, useEffect, useState, useRef } from 'react'

interface UseStorageProps<T> {
  key: string
  defaultValue: T
  serialize?: (val: T) => string
  deserialize?: (val: string) => T
  storage?: () => Storage
}

type UseStorageReturnType<T> = [T, (val: T) => void]

const localStorage = () => window?.localStorage

export const useLocalStorage = <T = unknown>(
  args: UseStorageProps<T>
): UseStorageReturnType<T> => useStorage({ ...args, storage: localStorage })

const sessionStorage = () => window?.sessionStorage

export const useSessionStorage = <T = unknown>(
  args: UseStorageProps<T>
): UseStorageReturnType<T> => useStorage({ ...args, storage: sessionStorage })

const useStorage = <T = unknown>({
  key,
  defaultValue,
  serialize = JSON.stringify,
  deserialize = JSON.parse,
  storage = localStorage,
}: UseStorageProps<T>): UseStorageReturnType<T> => {
  const getValueFromStorage = useCallback(() => {
    // `window` doesn't exist to the server, so this allows the code following
    // this check to run only in the client environment
    if (typeof window !== 'undefined') {
      const localValue = storage().getItem(key)

      if (localValue) {
        return deserialize(localValue)
      }
    }

    return null
  }, [deserialize, key, storage])

  const [state, setState] = useState<T>(defaultValue)

  useEffect(() => {
    const result = getValueFromStorage()

    if (result != null) {
      setState(result)
    }
  }, [getValueFromStorage])

  const prevKeyRef = useRef(key)

  const setValue = useCallback(
    (value: T) => {
      setState(value)

      if (typeof window === 'undefined') return

      const prevKey = prevKeyRef.current

      if (prevKey !== key) {
        storage().removeItem(prevKey)
      }

      prevKeyRef.current = key

      storage().setItem(key, serialize(value))
    },
    [key, serialize, storage]
  )

  return [state, setValue]
}
